<template>
    <app-main>
        <app-content>

            <div class="flex flex-wrap -mx-2">
                <div class="w-full md:w-1/2 px-2">
                    <!-- Start Title -->
                    <app-title
                        title="Services"
                    />
                    <!-- End Title -->
                </div>
                <div class="w-full md:w-1/2 px-2 md:text-right">
                    <app-button element="button" @click="$router.push({ name: 'services.new' })" class="btn btn--primary btn--primary--sml" v-if="checkPermission('services', 'create')">New Service</app-button>
                </div>
            </div>

            <app-box :loading="loading" :initial="loading" v-if="checkPermission('services', 'view')">
                <div class="flex flex-wrap -mx-2">
                    <div class="flex w-full px-2">
                        <app-field-text
                            v-model="search"
                            placeholder="Search Services"
                            class="w-full mr-4"
                            @input="searchServices"
                        />
                        <app-button
                            element="button"
                            @click="resetSearch"
                            :class="['flex whitespace-no-wrap items-center align-center btn btn--primary btn--primary--sml mb-5']"
                        >
                            Reset Search
                        </app-button>
                    </div>
                </div>

                <strong class="block mb-4">Publishable:</strong>
                <app-table v-if="services.length > 0">
                    <app-table-header>
                        <app-table-row :header="true">
                            <app-table-cell :header="true">
                                Title
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true">
                                Price
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true">
                                Published
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true">
                                Published At
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true" />
                        </app-table-row>
                    </app-table-header>

                    <app-table-body>
                        <app-table-row v-for="(s, i) in services" :key="i">
                            <app-table-cell>
                                {{ s.title }}

                                <router-link :to="{ name: 'services.drafts.home', params: { id: s.id } }" v-if="s.drafts.length > 0 && checkPermission('services', 'publishable')" class="mt-2 text-grey block text-xs">
                                    <span v-html="s.drafts.length" class="font-bold"></span> awaiting draft(s)
                                </router-link>
                            </app-table-cell>
                            <app-table-cell class="text-center">
                                {{ s.locale.currency }}{{ s.from_price }}
                            </app-table-cell>
                            <app-table-cell class="text-center">
                                <app-icon :icon="s.published ? 'check-circle' : 'times-circle'" :class="{'text-green': s.published, 'text-red': !s.published, 'text-lg': true}" />
                            </app-table-cell>
                            <app-table-cell class="text-center">
                                {{ moment(s.published_at).format('D MMM YY') }}
                                <span class="text-xs block mt-1 text-grey">({{ moment(s.published_at).fromNow() }})</span>
                            </app-table-cell>
                            <app-table-cell>
                                <router-link :to="{ name: 'services.edit', params: { id: s.id, page: $route.query.page ? $route.query.page : 1, search: $route.query.search ? $route.query.search : '' } }" class="mr-2 text-blue hover:text-blue-darker" v-if="s.children.length < 1">
                                    <app-icon icon="edit" v-if="checkPermission('services', 'edit')" />
                                </router-link>
                                <app-icon icon="trash" class="text-red cursor-pointer" @click="confirmModalOpened = true; confirmModalId = s.id" v-if="checkPermission('services', 'delete')" />
                            </app-table-cell>
                        </app-table-row>
                    </app-table-body>
                </app-table>

                <app-pagination-links
                    :response="response"
                    :resources="services"
                    @next="selectNextPage"
                    @previous="selectPreviousPage"
                />
            </app-box>

            <app-box :loading="loading" :initial="loading" class="mt-8" v-if="drafts.length > 0 && checkPermission('services', 'view') && checkPermission('services', 'publishable')">
                <strong class="block mb-4">Drafted:</strong>
                <app-table>
                    <app-table-header>
                        <app-table-row :header="true">
                            <app-table-cell :header="true">
                                Title
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true" />
                        </app-table-row>
                    </app-table-header>

                    <app-table-body>
                        <app-table-row v-for="(d, i) in drafts" :key="i">
                            <app-table-cell>
                                {{ d.title }}
                            </app-table-cell>
                            <app-table-cell>
                                <router-link :to="{ name: 'services.view', params: { id: d.id } }" class="mr-2 text-blue hover:text-blue-darker" v-if="checkPermission('services', 'edit')">
                                    <app-icon icon="eye" />
                                </router-link>
                                <app-icon icon="trash" class="text-red cursor-pointer" @click="confirmModalOpened = true; confirmModalId = d.id" v-if="checkPermission('services', 'delete')"/>
                            </app-table-cell>
                        </app-table-row>
                    </app-table-body>
                </app-table>
            </app-box>
        </app-content>

        <app-confirm-modal
            v-if="confirmModalOpened"
            @confirm="remove"
            @close="confirmModalOpened = false; confirmModalId = false"
            actionText="Yes please"
            cancelText="No thanks"
            heading="Hang on!"
            text="You are about to delete a service. Are you sure you wish to continue?"
        />
    </app-main>
</template>

<script>
    import _ from 'lodash';
    import { mapGetters } from 'vuex';
    /* API */
    import Api from '@/api/index.js';
    import Auth from '@/api/auth.js';
    /* Components */
    import Main from '@/components/Main';
    import Content from '@/components/template/Content';
    import Title from '@/components/Title';
    import Box from '@/components/Box';
    import Button from '@/components/Button';
    import PaginationLinks from '@/components/PaginationLinks';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import TextField from '@/components/fields/inputs/Text';

    /* Mixins */
    import Query from '@/mixins/query';
    import Paginatable from '@/mixins/paginatable';
    import Permissions from '@/mixins/permissions';

    export default {
        name: 'services.home',

        data: () => ({
            loading: true,
            services: [],
            drafts: [],
            response: null,
            confirmModalOpened: false,
            confirmModalId: false,
            search: ''
        }),

        mixins: [ Query, Paginatable, Permissions ],

        components: {
            'app-main': Main,
            'app-content': Content,
            'app-title': Title,
            'app-box': Box,
            'app-button': Button,
            'app-pagination-links': PaginationLinks,
            'app-confirm-modal': ConfirmModal,
            'app-field-text': TextField,
        },

        created() {
            if (this.$route.query.search && this.$route.query.search !== '') {
                this.search = this.$route.query.search;
            }
        },

        mounted() {
            this.getServices()
                .then(response => {
                    return this.getDraftServices();
                })
                .then(response => {
                    this.loading = false;
                });
        },

        methods: {
            searchServices: _.debounce(function() {
                this.$route.query.page = '1';
                return this.getServices();
            }, 500),

            resetSearch() {
                this.search = '';
                this.$route.query.search = '';
                this.$route.query.page = '1';
                this.getServices();
            },

            getServices() {
                let params = {};

                if (this.$route.query.page) {
                    params.page = this.$route.query.page;
                }

                if (this.search !== '') {
                    this.$route.query.search = this.search;
                    params.search = this.search;
                }

                return Api.request('get', `services?${this.prepareGetQueryString(params)}`)
                    .then(({ data }) => {
                        this.services = data.services;
                        this.response = data;
                    });
            },

            getDraftServices() {
                return Api.request('get', `services/drafts`)
                    .then(({ data }) => {
                        this.drafts = data.services;
                    });
            },

            remove() {
                this.confirmModalOpened = false;
                this.loading = true;

                this.deleteServices( this.confirmModalId ).then(response => {
                    return this.getServices();
                }).then(response => {
                    return this.getDraftServices();
                }).then(response => {
                    this.loading = false;
                    this.confirmModalId = false;
                });
            },

            deleteServices(id) {
                return Api.request('get', `services/delete/${id}`);
            },

            isTranslated(service, locale) {
                let translated = false;

                if (service.children.length > 0 && service.locale.flag != locale.flag) {
                    service.children.forEach(child => {
                        if (child.locale.flag == locale.flag) {
                            translated = true;
                        }
                    });
                }

                return translated;
            },

            getId(service, locale) {
                let id = service.id;

                if (service.children.length > 0 && service.locale.flag != locale.flag) {
                    service.children.forEach(child => {
                        if (child.locale.flag == locale.flag) {
                            id = child.id;
                        }
                    });
                }

                return id;
            }
        },

        computed: {
            ...mapGetters({
                locales: 'locales/items'
            })
        },
    }
</script>



// WEBPACK FOOTER //
// src/views/services/Home.vue